import { Component, effect, model, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { EditLinks } from '../../../../@core/constants/edit-links';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { ColumnConfigEx, ColumnDefinitionEx, PrimeColumnDef } from '../../../../@core/types/column-config-types-extended';
import { ProductUnitConversionLink, ProductUnitConversionLinkSearchInput, Unit, UnitConversion } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { UnitDropDownComponent } from '../../../../shared/controls/unit-drop-down/unit-drop-down.component';
import { ProductUnitConversionLinkService } from '../../../../shared/services/product-unit-conversion-link.service';

@Component({
  selector: 'app-unit-conversion-config-link-list-widget',
  templateUrl: './unit-conversion-config-link-list-widget.component.html',
  providers: [ProductUnitConversionLinkService],
  standalone: true,
  imports: [
    BaseListWrapperComponent,
    ButtonModule,
    TooltipModule,
    TranslatePipe,
    MessageModule,
    RouterModule
  ]
})
export class UnitConversionConfigLinkListWidgetComponent extends NewBaseListComponent<ProductUnitConversionLink> {

  override hasInactiveProperty = signal(true);

  doubleClickBaseUrl = EditLinks.UNIT_CONVERSION;
  vendorPriceListAddressLinkId = model<string>();
  productId = model<string>();
  variantParentId = model<string>();
  variantParentIdentifier = model<string>();
  baseUnit = model<Unit>();
  productUnitConversionConfigId = model<string>();
  enableConfigSafeButton = model<boolean>();
  selectionChanged = model<boolean>(false);
  enableGlobalDeletion = model<boolean>(true);
  origSelections = model<UnitConversion[]>([]);
  savingSig = model<boolean>(false);
  noEditOfNonVendorConversions = model<boolean>(false);

  unitDropDownComponent = signal<UnitDropDownComponent>(null);

  constructor(
    private productUnitConversionLinkService: ProductUnitConversionLinkService,
    private baseListService: BaseListCombinedService
  ) {
    super('ProductUnitConversionLink', productUnitConversionLinkService);
    this.noCopy = true;

    // deletion disabled when useGlobal item is selected since we dont want to be able to delete global items
    effect(() => {
      let enabledGlobalDeletion = this.enableGlobalDeletion();
      let globalItemsSelected = this.selectedItems().some(item => item.isGlobal);
      if (!enabledGlobalDeletion) {
        if (globalItemsSelected) {
          this.disabledDelete = true;
        }
        else {
          this.disabledDelete = false;
        }
      }
    });

    // sourceUnit dropdown disabled when product, vendor and base unit are set
    effect(() => {
      let productId = this.productId();
      let vendorPriceListAddressLinkId = this.vendorPriceListAddressLinkId();
      let baseUnit = this.baseUnit();
      let unitDropDown = this.unitDropDownComponent();
      untracked(() => {
        if (unitDropDown) {
          if ((productId || (vendorPriceListAddressLinkId && productId)) && baseUnit) {
            unitDropDown.disabled.set(true);
          }
          else {
            unitDropDown.disabled.set(false);
          }
        }
      });
    });

    // refresh if not autoloaded first
    effect(() => {
      let productId = this.productId();
      let vendorPriceListAddressLinkId = this.vendorPriceListAddressLinkId();
      let baseUnit = this.baseUnit();
      untracked(() => {
        if (!this.autoloadItems && baseUnit && (productId || (vendorPriceListAddressLinkId && productId))) {
          this.refresh();
        }
      });
    })

    effect(() => {
      let noEditOfNonVendorConversions = this.noEditOfNonVendorConversions();
      let selectedItems = this.selectedItems();
      let anySelectedWithoutVendorLink = selectedItems.some(item => !item?.vendorPriceListAddressLinkId);
      untracked(() => {
        if (noEditOfNonVendorConversions && anySelectedWithoutVendorLink) {
          this.disabledEdit = true;
          this.disabledDelete = true;
          this.noDoubleClick.set(true);
        }
        else {
          this.disabledEdit = false;
          this.disabledDelete = false;
          this.noDoubleClick.set(false);
        }
      });
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.variantParentId()) {
      this.noAddNew = true;
      this.noBaseEdit = true;
      this.noCopy = true;
      this.noDelete = true;
      this.inlineEditMode = false;
      this.noDoubleClick.set(true);
    }
  }

  override async onRowEditSave(rowData: ProductUnitConversionLink): Promise<ProductUnitConversionLink | null> {
    this.setLoading(true);

    // Ensure productId and vendorPriceListAddressLinkId are set
    if (!rowData.productId) {
      rowData.productId = this.productId();
    }
    if (!rowData.vendorPriceListAddressLinkId && this.vendorPriceListAddressLinkId()) {
      rowData.vendorPriceListAddressLinkId = this.vendorPriceListAddressLinkId();
    }

    try {
      // If this is a new item without an id, ensure required fields are set
      if (!rowData.id) {
        if (!rowData.productId) {
          rowData.productId = this.productId();
        }
      }

      // this is a new non-global item build by the global item
      if (rowData.isGlobal) {
        rowData.isGlobal = false;
        rowData.unitConversion.useGlobal = false;
        delete rowData.unitConversion.id;
        delete rowData.unitConversionId;
        delete rowData.id; //is just kind of placeholder
      }

      const saved = await super.onRowEditSave(rowData);

      // If a new item was created (global converted to specific), refresh and update selection
      if (saved?.id) {
        this.refresh();
      }
      this.setLoading(false);
      return saved ?? null;
    }
    catch (error) {
      console.error('Error saving unit conversion:', error);
      this.baseListService.notificationService.error(
        this.baseListService.i18nService.translate('error.unit-conversion-save-error')
      );
      this.setLoading(false);
      return null;
    }
  }

  override getSearchInputForQuery(): ProductUnitConversionLinkSearchInput {

    let si = super.getSearchInputForQuery() as ProductUnitConversionLinkSearchInput;

    if (this.vendorPriceListAddressLinkId()) {
      si.vendorPriceListAddressLinkId = this.vendorPriceListAddressLinkId();
    }
    if (this.productId()) {
      si.productId = this.productId();
    }
    // dont need to filter by this, since it should anyways be only saved with correct unit - and otherwise we need to see to delete
    // if (this.baseUnit()?.id) {
    //   si.sourceUnitId = this.baseUnit().id;
    // }
    if (this.variantParentId()) {
      si.variantParentId = this.variantParentId();
    }
    return si;
  }

  override getDisplayComponent(outputCell: HTMLElement, outputSpan: HTMLElement, col: PrimeColumnDef, rowData: ProductUnitConversionLink, row: HTMLTableRowElement) {
    super.getDisplayComponent(outputCell, outputSpan, col, rowData, row);
    if (col.field === 'unitConversion.rate') {
      if (rowData.unitConversion?.rate)
        outputSpan.textContent = rowData.unitConversion.rate.toFixed(8);
    }
  }

  protected setColumnConfig(colConfig: ColumnConfigEx): void {
    let cds = colConfig.columns;
    for (let cd of cds) {
      if (cd.field === 'unitConversion.sourceUnit.identifier') {
        cd.getEditComponent = () => UnitDropDownComponent;
        cd.editComponentCreated = (c: UnitDropDownComponent, rowData: ProductUnitConversionLink, colDef: ColumnDefinitionEx) => {
          c.setModel(rowData.unitConversion.sourceUnit);
          this.unitDropDownComponent.set(c);

          c.value.subscribe(async (x: Unit) => {
            //with this if check the detail widget is not recognized since it changes the rowData directly
            // if (x?.id && rowData.salesTaxCode?.id !== x.id) {
            rowData.unitConversion.sourceUnit = x;
            rowData.unitConversion.sourceUnitId = null;
            this.cdRef.markForCheck();
            // }
          });
        }
      }
      if (cd.field === 'unitConversion.targetUnit.identifier') {
        cd.getEditComponent = () => UnitDropDownComponent;
        cd.editComponentCreated = (c: UnitDropDownComponent, rowData: ProductUnitConversionLink, colDef: ColumnDefinitionEx) => {
          c.setModel(rowData.unitConversion.targetUnit);

          c.value.subscribe(async (x: Unit) => {
            rowData.unitConversion.targetUnit = x;
            rowData.unitConversion.targetUnitId = null;
            this.cdRef.markForCheck();
          });
        };
      }
      if (cd.field === 'unitConversion.useGlobal' || cd.field === 'isGlobal') {
        cd.getEditComponent = () => PhxCheckboxComponent;
        cd.editComponentCreated = (c: PhxCheckboxComponent, rowData: ProductUnitConversionLink, colDef: ColumnDefinitionEx) => {
          c.ngModel.set(rowData.isGlobal);
          c.disabled.set(true);
        };
      }
      if (cd.field === 'unitConversion.rate') {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: ProductUnitConversionLink, colDef: ColumnDefinitionEx) => {
          c.ngModel.set(rowData.unitConversion.rate);
          c.minFractionDigits.set(2);
          c.maxFractionDigits.set(8);
          c.ngModelChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
            if (x)
              rowData.unitConversion.rate = x;
          });
        };
      }
      super.setColumnConfig(colConfig);
    }
  }

  override addNewItem(index?: number, defaultValues?: ProductUnitConversionLink, event?: MouseEvent) {

    defaultValues = {
      ...defaultValues,
      unitConversion: {
        sourceUnit: this.baseUnit(),
        targetUnit: null,
        useGlobal: false,
        rate: 1,
        allowFractions: true,
      }
    };
    super.addNewItem(index, defaultValues);
  }

}

